import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, User as UserIcon, Save, ShieldCheck, Camera, Trash2, Lock, Eye, EyeOff, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_PICTURE_KB = 500;

/** Downscale + JPEG-compress an image file to fit under MAX_PICTURE_KB. Returns a data URL. */
async function compressToDataUrl(file, maxBytes = MAX_PICTURE_KB * 1024) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  // Square-crop to at most 512×512, then step down quality until under budget
  const size = Math.min(512, img.width, img.height);
  const sx = (img.width - size) / 2;
  const sy = (img.height - size) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = 512; canvas.height = 512;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, sx, sy, size, size, 0, 0, 512, 512);
  for (const q of [0.85, 0.7, 0.55, 0.4, 0.3]) {
    const out = canvas.toDataURL("image/jpeg", q);
    if (out.length * 0.75 < maxBytes) return out;
  }
  return canvas.toDataURL("image/jpeg", 0.3);
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ phone: "", province: "", city: "", picture: "", whatsapp_opt_in: false });
  const [initialForm, setInitialForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) return;
    const next = {
      phone: user.phone || "",
      province: user.province || "",
      city: user.city || "",
      picture: user.picture || "",
      whatsapp_opt_in: !!user.whatsapp_opt_in,
    };
    setForm(next);
    setInitialForm(next);
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = initialForm && Object.keys(form).some((k) => form[k] !== initialForm[k]);

  const pickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("That image is huge — please pick one under 8 MB"); return; }
    setUploading(true);
    try {
      const dataUrl = await compressToDataUrl(f);
      set("picture", dataUrl);
      toast.success("Preview ready — hit Save to publish");
    } catch (err) {
      logger.warn("compress failed:", err?.message);
      toast.error("Couldn't process that image — try another one");
    } finally { setUploading(false); }
  };

  const removePicture = () => {
    set("picture", "");
    toast.info("Picture removed — hit Save to publish");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!dirty || saving) return;
    const changes = {};
    for (const k of ["phone", "province", "city", "picture"]) {
      if (form[k] !== initialForm[k]) changes[k] = k === "picture" ? form[k] : (form[k] || "").trim();
    }
    if (form.whatsapp_opt_in !== initialForm.whatsapp_opt_in) {
      changes.whatsapp_opt_in = form.whatsapp_opt_in;
    }
    // Client-side E.164 validation (server also validates)
    const phoneToCheck = changes.phone !== undefined ? changes.phone : (form.phone || "").trim();
    if (phoneToCheck && !/^\+[1-9]\d{7,14}$/.test(phoneToCheck)) {
      toast.error("Phone must be in international format, e.g. +27821234567");
      return;
    }
    if (changes.whatsapp_opt_in === true && !phoneToCheck) {
      toast.error("Add a valid phone number before enabling WhatsApp reminders");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/profile", changes);
      await refreshUser?.();
      toast.success("Profile updated");
      setInitialForm({ ...form });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not save";
      toast.error(msg);
      logger.warn("profile save failed:", err?.message);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none";
  const labelCls = "text-xs uppercase font-extrabold tracking-[0.18em] text-[#7A7D96] mb-1.5 block";

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#7A7D96] hover:text-[#3D405B] mb-4" data-testid="profile-back-btn">
          <ArrowLeft size={14} /> Back
        </button>

        <header className="brand-card p-7 mb-6">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="relative">
              {form.picture ? (
                <img src={form.picture} alt="" className="w-20 h-20 rounded-2xl object-cover ring-2 ring-white shadow-md" data-testid="profile-avatar-img" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-accent flex items-center justify-center text-4xl font-extrabold text-[#3D405B]" data-testid="profile-avatar-fallback">{user.name?.[0]?.toUpperCase() || "?"}</div>
              )}
              <button type="button" onClick={pickFile} disabled={uploading} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-primary text-white shadow-md flex items-center justify-center hover:-translate-y-0.5 transition-all disabled:opacity-60" aria-label="Upload picture" data-testid="profile-picture-upload-btn">
                <Camera size={14} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFileChange} className="hidden" data-testid="profile-picture-input" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-1 flex items-center gap-2"><UserIcon size={13} /> Your profile</div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight">Edit details</h1>
              <p className="text-[#7A7D96] mt-1 truncate">Signed in as <b>{user.email}</b> · <span className="capitalize">{user.role}</span></p>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                <button type="button" onClick={pickFile} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5 disabled:opacity-60" data-testid="profile-picture-change">
                  <Camera size={12} /> {uploading ? "Processing…" : form.picture ? "Change picture" : "Upload picture"}
                </button>
                {form.picture && (
                  <button type="button" onClick={removePicture} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#7A7D96] hover:text-primary hover:bg-primary/5 font-bold" data-testid="profile-picture-remove">
                    <Trash2 size={12} /> Remove
                  </button>
                )}
                <span className="text-[#7A7D96]">PNG · JPG · WebP · auto-resized to under 500 KB</span>
              </div>
            </div>
          </div>
        </header>

        <form onSubmit={submit} className="brand-card p-7 space-y-5" data-testid="profile-form">
          <div className="p-4 rounded-2xl bg-primary/5 border border-primary/15 flex items-start gap-3 text-sm text-[#3D405B]" data-testid="profile-readonly-notice">
            <ShieldCheck size={18} className="text-primary shrink-0 mt-0.5" />
            <div>
              <strong>Most details below are read-only.</strong> You can update <b>phone</b>, <b>province</b>, <b>city</b>, and your WhatsApp preferences here.
              To change anything else, contact your admin.
            </div>
          </div>

          <div>
            <label htmlFor="p-name" className={labelCls}>Full name</label>
            <input id="p-name" value={user.name || ""} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-name-readonly" />
          </div>

          <div>
            <label htmlFor="p-email" className={labelCls}>Email <span className="normal-case font-bold text-[#7A7D96]">(contact admin to change)</span></label>
            <input id="p-email" value={user.email || ""} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-email" />
          </div>

          <div>
            <label htmlFor="p-role" className={labelCls}>Role</label>
            <input id="p-role" value={(user.role || "").replace(/^./, (c) => c.toUpperCase())} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-role-readonly" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="p-phone" className={labelCls}>Phone <span className="text-destructive">*</span></label>
              <input id="p-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+27821234567" type="tel" data-testid="profile-phone" />
              <p className="text-xs text-[#7A7D96] mt-1">International format, e.g. <span className="font-mono">+27821234567</span></p>
            </div>
            <div>
              <label htmlFor="p-province" className={labelCls}>Province</label>
              <select id="p-province" value={form.province} onChange={(e) => set("province", e.target.value)} className={inputCls} data-testid="profile-province">
                <option value="">— Select —</option>
                {["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"].map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="p-city" className={labelCls}>City</label>
              <input id="p-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} placeholder="e.g. Johannesburg" data-testid="profile-city" />
            </div>
            <div>
              <label htmlFor="p-suburb" className={labelCls}>Suburb <span className="normal-case font-bold text-[#7A7D96]">(auto-detected)</span></label>
              <input id="p-suburb" value={user.suburb || ""} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-suburb-readonly" />
            </div>
          </div>

          {user.age != null && (
            <div>
              <label className={labelCls}>Age</label>
              <input value={user.age} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-age-readonly" />
            </div>
          )}

          <div className="p-4 rounded-2xl bg-accent/15 border border-accent/40" data-testid="profile-whatsapp-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.whatsapp_opt_in}
                onChange={(e) => set("whatsapp_opt_in", e.target.checked)}
                className="mt-1 w-5 h-5 accent-primary rounded"
                data-testid="profile-whatsapp-optin"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-bold text-[#3D405B]">
                  <MessageCircle size={16} className="text-secondary" /> WhatsApp reminders
                </div>
                <p className="text-xs text-[#5F627D] mt-1 leading-relaxed">
                  {user.role === "counsellor"
                    ? "Get a WhatsApp message every morning at 08:00 SAST with today's schedule."
                    : "Get a WhatsApp reminder ~24 hours before each session, in addition to email."}
                  {" "}You can opt out any time by unchecking this or replying STOP to any message. Standard rates from your carrier may apply.
                </p>
                {form.whatsapp_opt_in && !form.phone?.trim() && (
                  <p className="text-xs text-destructive mt-2 font-bold">⚠ Add a phone number above before saving.</p>
                )}
              </div>
            </label>
          </div>

          {user.bio && (
            <div>
              <label className={labelCls}>Bio <span className="normal-case font-bold text-[#7A7D96]">(contact admin to update)</span></label>
              <textarea value={user.bio} disabled rows={4} className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed resize-none`} data-testid="profile-bio-readonly" />
            </div>
          )}

          {(user.professional_body || user.registration_number) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Professional body</label>
                <input value={user.professional_body || ""} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-professional-body-readonly" />
              </div>
              <div>
                <label className={labelCls}>Registration number</label>
                <input value={user.registration_number || ""} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-registration-readonly" />
              </div>
            </div>
          )}

          {(user.specialties?.length || user.languages?.length) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user.specialties?.length > 0 && (
                <div>
                  <label className={labelCls}>Specialties</label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="profile-specialties-readonly">
                    {user.specialties.map((s) => (
                      <span key={s} className="pill bg-primary/10 text-primary">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {user.languages?.length > 0 && (
                <div>
                  <label className={labelCls}>Languages</label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="profile-languages-readonly">
                    {user.languages.map((l) => (
                      <span key={l} className="pill bg-secondary/15 text-secondary">{l}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {user.office_address && (
            <div>
              <label className={labelCls}>Office address</label>
              <input value={user.office_address} disabled className={`${inputCls} bg-muted/40 text-[#7A7D96] cursor-not-allowed`} data-testid="profile-office-readonly" />
            </div>
          )}

          <div className="pt-2 flex items-center justify-between flex-wrap gap-3 border-t border-[#3D405B]/5">
            <p className="text-xs text-[#7A7D96] flex items-center gap-1.5"><ShieldCheck size={12} /> Changes are private and only visible to counsellors and admins.</p>
            <button type="submit" disabled={!dirty || saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:hover:translate-y-0" data-testid="profile-save-btn">
              <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </form>

        {user.role === "counsellor" && (
          <p className="text-xs text-[#7A7D96] text-center mt-5">
            Looking to update your office address or availability? Head to <Link to="/availability" className="text-primary font-bold hover:underline">Availability</Link>.
          </p>
        )}

        <PasswordSection />
      </main>
    </div>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!current || !next) { toast.error("Fill in both password fields"); return; }
    if (next.length < 10) { toast.error("New password must be at least 10 characters"); return; }
    if (next !== confirm) { toast.error("Confirmation doesn't match the new password"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/password", { current_password: current, new_password: next });
      const n = data?.other_sessions_revoked || 0;
      toast.success(n > 0 ? `Password changed — ${n} other session${n === 1 ? "" : "s"} signed out` : "Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not change password";
      toast.error(msg);
      logger.warn("change password failed:", err?.message);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none pr-11";
  const labelCls = "text-xs uppercase font-extrabold tracking-[0.18em] text-[#7A7D96] mb-1.5 block";
  const strength = next.length === 0 ? null : next.length < 10 ? "Weak" : next.length < 14 ? "OK" : "Strong";
  const strengthColor = strength === "Strong" ? "text-secondary" : strength === "OK" ? "text-[#B87A2D]" : "text-primary";

  return (
    <form onSubmit={submit} className="brand-card p-7 mt-6 space-y-5" data-testid="password-form">
      <div className="flex items-center gap-2 mb-2">
        <Lock size={18} className="text-primary" />
        <h2 className="text-xl font-extrabold text-[#3D405B]">Change password</h2>
      </div>
      <p className="text-sm text-[#7A7D96] -mt-2">Minimum 10 characters. Changing your password will sign you out of every other device you&apos;ve used.</p>

      <div>
        <label htmlFor="p-cur" className={labelCls}>Current password</label>
        <div className="relative">
          <input id="p-cur" type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} autoComplete="current-password" data-testid="password-current" />
        </div>
      </div>

      <div>
        <label htmlFor="p-new" className={labelCls}>New password</label>
        <div className="relative">
          <input id="p-new" type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} autoComplete="new-password" data-testid="password-new" />
          <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-[#7A7D96] hover:text-[#3D405B]" aria-label={show ? "Hide passwords" : "Show passwords"} data-testid="password-toggle-show">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {strength && (
          <p className={`text-xs mt-1.5 font-bold ${strengthColor}`} data-testid="password-strength">Strength: {strength}</p>
        )}
      </div>

      <div>
        <label htmlFor="p-conf" className={labelCls}>Confirm new password</label>
        <input id="p-conf" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" data-testid="password-confirm" />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#3D405B]/5">
        <p className="text-xs text-[#7A7D96] flex items-center gap-1.5"><ShieldCheck size={12} /> Verified server-side · other sessions revoked on success.</p>
        <button type="submit" disabled={saving || !current || !next || !confirm} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:hover:translate-y-0" data-testid="password-save-btn">
          <Lock size={14} /> {saving ? "Updating…" : "Update password"}
        </button>
      </div>
    </form>
  );
}
